<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CaregiverFilter } from '@/api/caregivers';
import type { PatientFilter } from '@/api/patients';
import type { SerializedUser } from '@/api/users';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useGroupFilterVisible } from '@/composables/useGroupFilterVisible';
import { useMobile } from '@/composables/useMobile';
import { usePermissions } from '@/composables/usePermissions';
import { useTableFilter } from '@/composables/useTableFilter';
import { formatCurrency } from '@/utils/currency';
import GroupFilterSelect from '@/components/group-filter-select.vue';

interface Props {
  patients?: PatientFilter[];
  caregivers?: CaregiverFilter[];
  showGroupByFilter: boolean;
  showCareerFilter: boolean;
}

defineProps<Props>();

// Selections flow up through one model per filter (calendar-v2-filters
// pattern); the page owns the Pinia sync and the query commit.
const drawer = defineModel<boolean>('drawer', { default: false });
const patientId = defineModel<number | null | undefined>('patientId');
const caregiverId = defineModel<number | null | undefined>('caregiverId');
const startDateTimeFilter = defineModel<string | undefined>('startDateTimeFilter');
const groupByClass = defineModel<'patient' | 'caregiver' | undefined>('groupByClass');
const careerIn = defineModel<string[]>('careerIn', { default: () => [] });
const groupIds = defineModel<number[]>('groupIds', { default: () => [] });
// The page's commit guard needs to know while an autocomplete is focused
// without a selection, so focus state is exposed as two boolean models.
const patientFilterFocused = defineModel<boolean>('patientFilterFocused', { default: false });
const caregiverFilterFocused = defineModel<boolean>('caregiverFilterFocused', { default: false });

const emit = defineEmits<{
  apply: [payload: { priceFilter: string, costFilter: string, markedStatusFilter: string }],
}>();

const { t } = useI18n();
const { isMobile } = useMobile();
const { hasPermission } = usePermissions();
const { accessibleCareers } = useAccessibleCareers();
const { shouldShow: showGroupFilter } = useGroupFilterVisible();
const nameFilter = useTableFilter(['fullName']);
const currentUser = inject<Ref<SerializedUser>>('currentUser');

const rail = ref(false);

const startDateTimeFilterOptions = computed(() => [
  {
    title: t('shiftsCalendar.filters.vista.daily'),
    value: 'today',
  },
  {
    title: t('shiftsCalendar.filters.vista.weekly'),
    value: 'weekly',
  },
  {
    title: t('shiftsCalendar.filters.vista.monthly'),
    value: 'monthly',
  },
]);

const groupByClassOptions = computed(() => [
  {
    title: t('shiftsCalendar.filters.groupBy.caregiver'),
    value: 'caregiver',
  },
  {
    title: t('shiftsCalendar.filters.groupBy.patient'),
    value: 'patient',
  },
]);

// Only careers the user can read, plus "Sin Especialidad" — the backend scope
// (CaregiverPolicy::Scope) always keeps caregivers with no career, so it stays
// selectable regardless of permissions.
const careerOptions = computed(() => [
  ...accessibleCareers.value,
  {
    title: t('shiftsCalendar.filters.withoutCareer'),
    value: 'without_career',
  },
]);

const markedStatusFilterOptions = computed(() => [
  {
    title: t('shiftsCalendar.filters.markedStatus.all'),
    value: '',
  },
  {
    title: t('shiftsCalendar.filters.markedStatus.marked'),
    value: 'marked',
  },
  {
    title: t('shiftsCalendar.filters.markedStatus.unmarked'),
    value: 'unmarked',
  },
]);

// Advanced filters are drafts: they only reach the page (and the query) when
// the user applies them.
const advancedFiltersOpen = ref(false);
const currentPriceFilter = ref<string>('');
const currentCostFilter = ref<string>('');
const currentPriceFilterFormatted = ref<string>('');
const currentCostFilterFormatted = ref<string>('');
const currentMarkedStatusFilter = ref<string>('');

function formatFilterInput(filterRef: Ref<string>, formattedRef: Ref<string>) {
  return (e: Event) => {
    const numericValue = (e.target as HTMLInputElement).value.replace(/[^\d]/g, '');
    filterRef.value = numericValue;
    formattedRef.value = numericValue ?
      formatCurrency(parseInt(numericValue, 10)).replace('$ ', '') : '';
  };
}

const handlePriceInput = formatFilterInput(currentPriceFilter, currentPriceFilterFormatted);
const handleCostInput = formatFilterInput(currentCostFilter, currentCostFilterFormatted);

function applyAdvancedFilters() {
  emit('apply', {
    priceFilter: currentPriceFilter.value,
    costFilter: currentCostFilter.value,
    markedStatusFilter: currentMarkedStatusFilter.value,
  });
}

function clearAdvancedFilters() {
  currentPriceFilter.value = '';
  currentCostFilter.value = '';
  currentPriceFilterFormatted.value = '';
  currentCostFilterFormatted.value = '';
  currentMarkedStatusFilter.value = '';
}
</script>
<template>
  <v-navigation-drawer
    v-model="drawer"
    :rail="!isMobile && rail"
    :permanent="!isMobile"
    :temporary="isMobile"
    @click="rail = false"
  >
    <v-list-item
      :title="t('shiftsCalendar.filters.title')"
      nav
    >
      <template #prepend>
        <v-btn
          icon="mdi-filter"
          variant="text"
          slim
          size="small"
          @click.stop="rail = !rail"
        />
      </template>
      <template #append>
        <v-btn
          icon="mdi-chevron-left"
          variant="text"
          @click.stop="isMobile ? (drawer = false) : (rail = !rail)"
        />
      </template>
    </v-list-item>
    <v-divider />
    <v-list-item
      v-if="showGroupFilter"
      :class="rail ? 'd-sr-only mt-4' : 'mt-4'"
    >
      <p class="mb-2 font-weight-bold">
        {{ t('shiftsCalendar.filters.group') }}
      </p>
      <group-filter-select
        v-model="groupIds"
        :label="t('shiftsCalendar.filters.groupLabel')"
        variant="outlined"
        :max-visible-chips="99"
      />
    </v-list-item>
    <v-list-item :class="[rail ? 'd-sr-only' : '', showGroupFilter ? '' : 'mt-4']">
      <p class="mb-2 font-weight-bold">
        {{ t('shiftsCalendar.patient') }}
      </p>
      <v-autocomplete
        v-model="patientId"
        :items="patients"
        item-title="fullName"
        item-value="id"
        variant="outlined"
        bg-color="white"
        class="text-subtitle-1"
        clearable
        persistent-clear
        :no-data-text="t('shiftsCalendar.filters.noPatients')"
        :custom-filter="nameFilter"
        @focus="patientFilterFocused = true"
        @blur="patientFilterFocused = false"
      />
    </v-list-item>
    <v-list-item :class="rail ? 'd-sr-only' : ''">
      <p class="mb-2 font-weight-bold">
        {{ t('shiftsCalendar.caregiver') }}
      </p>
      <v-autocomplete
        v-model="caregiverId"
        :items="caregivers"
        item-title="fullName"
        item-value="id"
        variant="outlined"
        bg-color="white"
        clearable
        persistent-clear
        :no-data-text="t('shiftsCalendar.filters.noCaregivers')"
        :custom-filter="nameFilter"
        @focus="caregiverFilterFocused = true"
        @blur="caregiverFilterFocused = false"
      />
    </v-list-item>
    <v-list-item :class="rail ? 'd-sr-only' : ''">
      <p class="mb-2 font-weight-bold">
        {{ t('shiftsCalendar.filters.vista.label') }}
      </p>
      <v-select
        v-model="startDateTimeFilter"
        :items="startDateTimeFilterOptions"
        item-title="title"
        item-value="value"
        variant="outlined"
        bg-color="white"
        class="text-subtitle-1"
        persistent-clear
      />
    </v-list-item>
    <v-list-item
      v-if="showGroupByFilter"
      :class="rail ? 'd-sr-only' : ''"
    >
      <p class="mb-2 font-weight-bold">
        {{ t('shiftsCalendar.filters.groupBy.label') }}
      </p>
      <v-select
        v-model="groupByClass"
        :items="groupByClassOptions"
        item-title="title"
        item-value="value"
        variant="outlined"
        bg-color="white"
        class="text-subtitle-1"
        persistent-clear
      />
    </v-list-item>
    <v-list-item
      v-if="showCareerFilter"
      :class="rail ? 'd-sr-only' : ''"
    >
      <p class="mb-2 font-weight-bold">
        {{ t('shiftsCalendar.filters.career') }}
      </p>
      <v-select
        v-model="careerIn"
        :items="careerOptions"
        item-title="title"
        item-value="value"
        variant="outlined"
        bg-color="white"
        class="text-subtitle-1"
        multiple
        chips
        clearable
        persistent-clear
      />
    </v-list-item>
    <div
      v-if="hasPermission('shifts', 'read_financial_information')"
      class="mt-auto"
    >
      <v-divider />
      <v-list-item>
        <v-btn
          variant="text"
          block
          class="justify-start text-caption"
          @click="advancedFiltersOpen = !advancedFiltersOpen"
        >
          <v-icon
            :icon="advancedFiltersOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            class="mr-2"
            size="small"
          />
          {{ t('shiftsCalendar.filters.advanced') }}
        </v-btn>
        <v-slide-y-transition>
          <div
            v-show="advancedFiltersOpen"
            class="mt-3"
          >
            <v-text-field
              v-model="currentPriceFilterFormatted"
              :label="t('shiftsCalendar.filters.price')"
              variant="outlined"
              bg-color="white"
              density="compact"
              hide-details
              class="mb-2"
              prefix="$"
              @input="handlePriceInput"
              @keyup.enter="applyAdvancedFilters"
            />
            <v-text-field
              v-model="currentCostFilterFormatted"
              :label="t('shiftsCalendar.filters.cost')"
              variant="outlined"
              bg-color="white"
              density="compact"
              hide-details
              class="mb-2"
              prefix="$"
              @input="handleCostInput"
              @keyup.enter="applyAdvancedFilters"
            />
            <v-select
              v-if="currentUser?.companyIncludeCheckedInShiftsOnly"
              v-model="currentMarkedStatusFilter"
              :items="markedStatusFilterOptions"
              item-title="title"
              item-value="value"
              variant="outlined"
              bg-color="white"
              density="compact"
              hide-details
              class="mb-2"
            />
            <div class="d-flex justify-center gap-2">
              <v-btn
                variant="outlined"
                color="primary"
                size="small"
                class="ma-1"
                @click="applyAdvancedFilters"
              >
                {{ t('shiftsCalendar.filters.apply') }}
              </v-btn>
              <v-btn
                variant="outlined"
                color="secondary"
                size="small"
                class="ma-1"
                @click="() => {clearAdvancedFilters(); applyAdvancedFilters();}"
              >
                {{ t('shiftsCalendar.filters.clear') }}
              </v-btn>
            </div>
          </div>
        </v-slide-y-transition>
      </v-list-item>
    </div>
  </v-navigation-drawer>
</template>
